/**
 * useGlobalSearch Hook
 *
 * Provides global search functionality with debouncing.
 * Searches across projects, issues, and users.
 */

import { useQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { api } from '@/lib/api';
import { searchKeys } from '@/lib/query-keys';

const DEBOUNCE_MS = 300;
const MIN_QUERY_LENGTH = 2;

// ============================================================================
// Types
// ============================================================================

export interface ProjectSearchResult {
  id: string;
  key: string;
  name: string;
  description: string | null;
  /** Absent — not null — for a cross-tenant guest on a shared project. */
  tenant?: { id: string; name: string };
}

export interface IssueSearchResult {
  id: string;
  displayId: string;
  title: string;
  status: string;
  priority: string;
  type?: string | null;
  dueDate?: string | null;
  assignee?: { id: string; name: string } | null;
  project: { id: string; name: string; key: string };
}

export interface UserSearchResult {
  id: string;
  name: string;
  email: string;
  role: string;
  profilePictureUrl: string | null;
  tenant: { id: string; name: string } | null;
}

export interface SearchResults {
  projects: ProjectSearchResult[];
  issues: IssueSearchResult[];
  users: UserSearchResult[];
}

export type FlatSearchResult =
  | ({ type: 'project' } & ProjectSearchResult)
  | ({ type: 'issue'; issueType?: string | null } & Omit<IssueSearchResult, 'type'>)
  | ({ type: 'user' } & UserSearchResult);

// ============================================================================
// Hook
// ============================================================================

export function useGlobalSearch() {
  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');

  // Debounce the query
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedQuery(query);
    }, DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [query]);

  // Fetch search results
  const { data, isLoading, isFetching } = useQuery({
    queryKey: searchKeys.results(debouncedQuery),
    queryFn: async () => {
      const response = await api.get<SearchResults>(
        `/search?q=${encodeURIComponent(debouncedQuery)}&limit=5`,
      );
      return response.data ?? { projects: [], issues: [], users: [] };
    },
    enabled: debouncedQuery.length >= MIN_QUERY_LENGTH,
    staleTime: 30000, // 30 seconds
  });

  // Flatten results for keyboard navigation
  const flatResults = useMemo<FlatSearchResult[]>(() => {
    if (!data) return [];
    return [
      ...data.projects.map((p) => ({ type: 'project' as const, ...p })),
      ...data.issues.map(({ type: issueType, ...rest }) => ({
        type: 'issue' as const,
        issueType,
        ...rest,
      })),
      ...data.users.map((u) => ({ type: 'user' as const, ...u })),
    ];
  }, [data]);

  // Check if we have any results
  const hasResults = flatResults.length > 0;

  // Check if query meets minimum length
  const isQueryValid = query.length >= MIN_QUERY_LENGTH;

  // Reset query
  const resetQuery = () => {
    setQuery('');
    setDebouncedQuery('');
  };

  return {
    query,
    setQuery,
    debouncedQuery,
    results: data,
    flatResults,
    isLoading: isLoading && isQueryValid,
    isFetching: isFetching && isQueryValid,
    hasResults,
    isQueryValid,
    resetQuery,
  };
}
